import {
	Box,
	CircularProgress,
	FormControl,
	Grid,
	ListItemText,
	MenuItem,
	Select,
	Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { FilterData } from '../../../../containers/actions/networks_revised';
import { decodeBase64 } from '../../../../libs/HelperContent';

interface CustomRelationsFilterProps {
	labelText?: string;
	value?: string;
	onChange?: (value: string) => void;
}

const CustomRelationsFilter: React.FC<CustomRelationsFilterProps> = ({
	labelText = 'Relations',
	value,
	onChange
}) =>
{
	const [ searchParams, setSearchParams ] = useSearchParams();
	const queryParamValue = searchParams.get('relationFilter');
	// local value is decrypted value of the query param value
	const [ localValue, setLocalValue ] = useState<string>(decodeBase64(queryParamValue || '') || 'all');
	const [ menuOpen, setMenuOpen ] = useState<boolean>(false);
	const [ loading, setLoading ] = useState<boolean>(false);
	const [ relationData, setRelationData ] = useState<string[]>([]);
	const [ error, setError ] = useState<string | null>(null);

	const isControlled = value !== undefined && onChange !== undefined;
	const selectedValue = isControlled ? value : localValue;

	// Get session data from Redux store
	const session = useSelector((state: any) => state?.mysession?.session);
	const companyId = session?.companyId || '';

	// Fetch relations data using the same approach as FilterSlider
	const getFliterData = async () =>
	{
		if (!companyId) return;

		const query = {
			companyId : session?.companyId,
			empRole   : session?.empRole,
			type      : 'connections'
		};

		try
		{
			setLoading(true);
			setError(null);
			const res = await FilterData(query);

			if (res?.status && res?.data?.relations)
			{
				// Store relations directly as strings
				setRelationData(res.data.relations);
			}
			else
			{
				setError('No relations data available');
				setRelationData([]);
			}
		}
		catch (err)
		{
			console.error('Error fetching relations:', err);
			setError('Failed to load relations data');
			setRelationData([]);
		}
		finally
		{
			setLoading(false);
		}
	};

	// Initialize from URL query param if not controlled
	useEffect(() =>
	{
		if (!isControlled)
		{
			setLocalValue(decodeBase64(queryParamValue || '') || 'all');
		}
	}, [ queryParamValue ]);

	// Fetch relations data when component mounts or companyId changes
	useEffect(() =>
	{
		if (companyId)
		{
			getFliterData();
		}
	}, [ companyId ]);

	// Filter relations based on search text
	useEffect(() =>
	{
		// setFilteredRelations(relationData);
	}, [ relationData ]);

	const handleSelectChange = (value: string) =>
	{
		if (isControlled)
		{
			onChange(value);
		}
		else
		{
			setLocalValue(value);
			searchParams.set('relationFilter', window.btoa(value));
			setSearchParams(searchParams);
		}

		setMenuOpen(false);
	};

	const renderSelectedValue = () =>
	{
		if (selectedValue === 'all') return 'All';

		// Find the selected relation string
		const selectedRelation = relationData.find(
			(relation) => relation === selectedValue
		);

		if (selectedRelation)
		{
			return (
				<Grid container direction='row' display={'flex'} alignItems='center'>
					<Grid item minWidth={0} px={1}>
						<Typography overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap' maxWidth='200px' fontSize='var(--pixel14)'>
							{selectedRelation}
						</Typography>
					</Grid>
				</Grid>
			);
		}

		return 'All';
	};

	return (
		<Grid item>
			<Typography color='var(--darkgray)' fontSize='var(--pixel10)'>
				{labelText}
			</Typography>
			<FormControl fullWidth>
				<Select
					value={selectedValue}
					onChange={(event) => handleSelectChange(event.target.value as string)}
					renderValue={renderSelectedValue}
					open={menuOpen}
					onOpen={() => setMenuOpen(true)}
					onClose={() => setMenuOpen(false)}
					sx={{
						backgroundColor                      : 'var(--lightgray)',
						border                               : 'none',
						borderRadius                         : 'var(--pixel4)',
						boxShadow                            : 'none',
						minWidth                             : '220px',
						maxWidth                             : '200px',
						width                                : '100%',
						'& .MuiOutlinedInput-notchedOutline' : {
							border : 'none'
						},
						'&:hover' : {
							backgroundColor : '#3FBBE11A'
						},
						'& .MuiSelect-select' : {
							padding : '4px 10px'
						}
					}}
					MenuProps={{
						disableAutoFocusItem : true,
						PaperProps           : {
							style       : { maxHeight: 300, overflow: 'auto', maxWidth: '220px', width: '100%' },
							onMouseDown : (e) => e.stopPropagation()
						},
						MenuListProps : {
							sx : {
								paddingTop : '0px',
								marginTop  : '0px',
								'& ul'     : {
									paddingTop : '0px !important'
								}
							}
						}
					}}
				>
					<MenuItem onClick={() => handleSelectChange('all')} value='all'>
						{/* <ListItemText primary='All' /> */}
						<Grid container direction='row' py={0.5} display={'flex'} alignItems='center'>
							<Grid item minWidth={0} px={1}>
								<Typography>All</Typography>
							</Grid>
						</Grid>
					</MenuItem>

					{relationData.map((relation) => (
						<MenuItem
							key={relation}
							value={relation}
							onClick={() => handleSelectChange(relation)}
						>
							<Grid container direction='row' py={0.5} display={'flex'} alignItems='center'>
								<Grid item minWidth={0} px={1}>
									<Typography>{relation}</Typography>
								</Grid>
							</Grid>
						</MenuItem>
					))}

					{relationData.length === 0 && !loading && (
						<MenuItem disabled>
							<Typography color='textSecondary'>No results found</Typography>
						</MenuItem>
					)}

					{loading && (
						<Box
							sx={{
								display        : 'flex',
								alignItems     : 'center',
								justifyContent : 'center',
								py             : 'var(--pixel16)'
							}}
						>
							<CircularProgress size={20} />
						</Box>
					)}

					{error && (
						<MenuItem disabled>
							<Typography color='error'>{error}</Typography>
						</MenuItem>
					)}
				</Select>
			</FormControl>
		</Grid>
	);
};

export default CustomRelationsFilter;